import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { customAuth } from '../services/customAuth';
import { ProfileService } from '../services/profileService';
import LoadingSpinner from '../components/LoadingSpinner';
import { useDialog } from './DialogContext';

interface User {
  name: string;
  email: string;
  role?: 'admin' | 'user';
  isAdmin?: boolean;
  sub?: string; // For compatibility with existing code
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: () => void;
  logout: (onLogoutComplete?: () => void) => void;
  handleAuthCallback: () => Promise<void>;
  forceClearAuth: () => void;
  showCustomLogin: () => void;
  refreshUserState: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Role-based admin check function
const isAdminUser = (user: User): boolean => {
  return user.role === 'admin';
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showAlert } = useDialog();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check for error parameters in URL (from redirect failures)
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        
        if (error) {
          console.error('Authentication error from URL:', error);
          // Clear the error from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Show user-friendly error message
          let errorMessage = 'Authentication failed. Please try again.';
          switch (error) {
            case 'no_code':
              errorMessage = 'Sign-in session expired. Please try signing in again.';
              break;
            case 'token_exchange_failed':
              errorMessage = 'Unable to complete login. Please try again.';
              break;
            case 'no_token':
              errorMessage = 'Sign-in verification failed. Please try again.';
              break;
            case 'auth_failed':
              errorMessage = 'Authentication failed. Please try again.';
              break;
            default:
              errorMessage = 'An error occurred during login. Please try again.';
          }
          
          // You could show this in a modal or alert
          alert(errorMessage);
          return;
        }
        
                // Check for existing session using custom authentication
        const currentUser = await customAuth.checkAuth();
        if (currentUser) {
          const userWithAdmin: User = {
            ...currentUser,
            isAdmin: isAdminUser(currentUser),
            sub: currentUser.email // Use email as sub for compatibility
          };
          setUser(userWithAdmin);
        } else {
          setUser(null);
        }
          
        // Update last login timestamp if user exists
        if (currentUser) {
          try {
            await ProfileService.updateLastLogin();
          } catch (error) {
            console.error('Error updating last login:', error);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Add admin status to user when user changes
  useEffect(() => {
    if (user && user.isAdmin === undefined) {
      const userWithAdmin: User = {
        ...user,
        isAdmin: isAdminUser(user),
        sub: user.email // Use email as sub for compatibility
      };
      setUser(userWithAdmin);
    }
  }, [user]);

  const login = async () => {
    try {
      // Custom authentication will be handled by components that call this function
      // They will show the custom login modal instead of redirecting
      console.log('Custom authentication login triggered');
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const logout = (onLogoutComplete?: () => void) => {
    // Show logout confirmation message
    showAlert({
      title: 'Signing out...',
      message: 'You have been successfully signed out. Returning to home page.',
      okText: 'OK',
      variant: 'success',
      onClose: () => {
        // Perform actual logout after user acknowledges the message
        customAuth.logout();
        setUser(null);
        // Call the completion callback if provided
        if (onLogoutComplete) {
          onLogoutComplete();
        }
      }
    });
  };

  const handleAuthCallback = async () => {
    try {
      // Custom authentication doesn't use callbacks - it's handled directly
      console.log('Custom authentication callback not needed');
    } catch (error) {
      console.error('Auth callback error:', error);
    }
  };

  const forceClearAuth = () => {
    customAuth.logout();
    setUser(null);
  };

  const showCustomLogin = () => {
    // This will be handled by components that need to show the login modal
    console.log('Show custom login modal');
  };

  const refreshUserState = async () => {
    try {
              const currentUser = await customAuth.checkAuth();
      setUser(currentUser);
    } catch (error) {
      console.error('Error refreshing user state:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    handleAuthCallback,
    forceClearAuth,
    showCustomLogin,
    refreshUserState
  };

  // Show loading spinner while initializing auth
  if (isLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <LoadingSpinner size="large" message="Initializing..." />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 